import React, { useState } from 'react';
import { X, Download, Check, Loader2 } from 'lucide-react';

const serif = { fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 };

// Email-gated PDF download modal for white papers. Records the lead, then opens the real PDF.
export const WhitePaperGateModal = ({ pub, onClose }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | submitting | done | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'submitting') return;
    setStatus('submitting');
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/whitepaper/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, slug: pub.slug, title: pub.title, summary: pub.excerpt, pdf: pub.pdf }),
      });
      setStatus('done');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[#1F1812]/60 backdrop-blur-sm p-4"
      data-testid="whitepaper-gate-overlay"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-[440px] bg-[#FDF8F3] border border-[#2A2018]/[0.08] rounded-2xl p-8 md:p-10 shadow-[0_40px_90px_-30px_rgba(42,32,24,0.45)]"
        onClick={(e) => e.stopPropagation()}
        data-testid="whitepaper-gate-modal"
      >
        <button
          onClick={onClose}
          data-testid="whitepaper-gate-close"
          className="absolute top-5 right-5 text-[#7A5A2E] hover:text-[#1F1812] transition-colors"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        {status === 'done' ? (
          <div className="text-center py-4" data-testid="whitepaper-gate-success">
            <div className="w-12 h-12 rounded-full bg-[#EDE3D5] flex items-center justify-center mx-auto mb-5">
              <Check size={20} className="text-[#7A5A2E]" />
            </div>
            <h3 className="text-[#1F1812]" style={{ ...serif, fontSize: '24px' }}>Check your inbox</h3>
            <p className="mt-3 text-[#5C4F42] text-[14px] leading-[1.6]">
              We've sent the PDF to <span className="text-[#1F1812] font-medium">{email}</span>. If it doesn't arrive in a few minutes, check your spam folder or <a href={pub.pdf} target="_blank" rel="noopener noreferrer" className="text-[#B8864B] underline underline-offset-2">click here</a> to open it directly.
            </p>
          </div>
        ) : (
          <>
            <div className="w-11 h-11 rounded-full bg-[#EDE3D5] flex items-center justify-center mb-5">
              <Download size={18} className="text-[#7A5A2E]" />
            </div>
            <h3 className="text-[#1F1812]" style={{ ...serif, fontSize: '26px', lineHeight: 1.15 }}>
              {pub.slug === 'decision-debt-31-billion-problem' ? 'The full research on decision debt.' : `Get the full research: ${pub.title}`}
            </h3>
            <p className="mt-2 text-[#5C4F42] text-[14px] leading-[1.6]">
              {pub.slug === 'decision-debt-31-billion-problem'
                ? "We'll email you the PDF. Every undocumented approval, scope change, and verbal agreement is a liability that comes due eventually. This is the research on what it costs firms like yours - and what the ones that avoid it do differently."
                : `We'll email you the PDF. ${pub.excerpt || ''}`}
            </p>
            <p className="mt-3 text-[#5C4F42] text-[14px] leading-[1.6]">
              You'll also get Paper Trail, practical notes for design firms, a few times a month.
            </p>
            <form onSubmit={handleSubmit} className="mt-6">
              <input
                data-testid="whitepaper-gate-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@studio.com"
                className="w-full bg-white border border-[#E2D9CB] text-[#1F1812] placeholder-[#A89888] text-[15px] px-4 py-3.5 rounded-md focus:outline-none focus:border-[#C49A57] transition-colors"
              />
              {status === 'error' && (
                <p className="mt-2 text-[#B04A3B] text-[13px]" data-testid="whitepaper-gate-error">
                  Something went wrong. Please try again.
                </p>
              )}
              <button
                type="submit"
                disabled={status === 'submitting'}
                data-testid="whitepaper-gate-submit"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#1F1812] hover:bg-[#2A2018] disabled:opacity-70 text-white text-[14px] font-semibold px-6 py-3.5 rounded-md transition-colors"
              >
                {status === 'submitting' ? (
                  <><Loader2 size={15} className="animate-spin" /> Sending...</>
                ) : (
                  <><Download size={15} /> Send Me the PDF</>
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default WhitePaperGateModal;
